import {
  isValidationProblemDetail,
  ValidationProblemDetails,
} from "@utdanningsdirektoratet/utils/object";
import {
  useDeepCompareEffect,
  useDeepCompareMemo,
} from "@utdanningsdirektoratet/hooks";
import { dequal as deepEqual } from "dequal";
import isEqual from "fast-deep-equal";
import * as React from "react";
import {
  Field,
  Fields,
  FormValidation,
  MappedProps,
  Message,
  ValidationResult,
  ValidationType,
  Values,
  Options,
  UPDATE_FIELD,
  RESET_FIELDS,
  ADD_UNSAVEDCHANGES,
  REMOVE_UNSAVEDCHANGES,
  RESET_UNSAVEDCHANGES,
  ALL_VALIDATED,
  VALIDATE_FIELDS,
  ReducerType,
} from "./types";

const messageOrMessageFunctionOutput = <T extends Record<string, any>>(
  message: Message<T>,
  value: T[keyof T],
  values: Values<T>,
): string => {
  return typeof message === "function" ? message(value, values) : message;
};

const getEmptyValue = (value: any) => {
  switch (typeof value) {
    case "string":
      return "";
    case "number":
      return 0;
    case "boolean":
      return false;
    case "object": {
      if (Array.isArray(value)) return [];
      return null;
    }
    case "undefined":
      return undefined;
    default:
      return null;
  }
};

const getEventValue = <P, T extends HTMLElement>(
  event: React.ChangeEvent<T> | P[keyof P] | undefined,
): P[keyof P] | string | number | boolean | undefined => {
  if (!event) return event;
  if ((event as React.SyntheticEvent<T>).target) {
    if (
      ((event as React.SyntheticEvent<T>).target as HTMLInputElement).type ===
        "checkbox" ||
      ((event as React.SyntheticEvent<T>).target as HTMLInputElement).type ===
        "radio"
    ) {
      return ((event as React.SyntheticEvent<T>).target as HTMLInputElement)
        .checked;
    }
    return ((event as React.SyntheticEvent<T>).target as HTMLInputElement)
      .value;
  }
  return event as P[keyof P];
};

const getValues = <P extends Record<string, any>>(
  state: StateType<P>,
  props: MappedProps<P>,
  options: Options,
): Values<P> => {
  return Object.keys(state.fields || {}).reduce((acc, key) => {
    if (
      props[key]?.emptyStringAsNull ||
      (options.emptyStringAsNull && props[key]?.emptyStringAsNull !== false)
    ) {
      return {
        ...acc,
        [key]: state.fields[key].value === "" ? null : state.fields[key].value,
      };
    }
    return { ...acc, [key]: state.fields[key].value };
  }, {} as Values<P>);
};

const getFieldValue = <T extends Record<string, any>>(
  value: T[keyof T] | undefined,
): T[keyof T] => {
  if ((value as T[keyof T]) === "null") return null as T[keyof T];
  if ((value as T[keyof T]) === "undefined") return undefined as T[keyof T];
  if ((value as T[keyof T]) === 0) return 0 as T[keyof T];
  if ((value as T[keyof T]) === "true") return true as T[keyof T];
  if ((value as T[keyof T]) === "false") return false as T[keyof T];
  return value as T[keyof T];
};

const arrayValidateField = <T extends Record<string, any>>(
  validationObj: Array<ValidationType<T, T[keyof T]>>,
  value: T[keyof T],
  values: T,
): ValidationResult<T> => {
  let result = { error: false } as ValidationResult<T>;

  validationObj?.some((val) => {
    const error = !val.validation(value, values);
    if (error) {
      result = {
        error: true,
        message: messageOrMessageFunctionOutput(val.message, value, values),
      };
      return true;
    }
    return false;
  });

  return result;
};

const validateField = <T extends Record<string, any>>(
  mappedProps: MappedProps<T>,
  fieldName: keyof T,
  value: T[keyof T],
  values: T,
): ValidationResult<T> => {
  const validationObj = mappedProps[fieldName].validation ?? [];
  return arrayValidateField<T>(validationObj, value, values);
};

export type StateType<T> = {
  unsavedChanges: Set<keyof T>;
  allValidated: boolean;
  fields: {
    [K in keyof T]: {
      value: T[K];
      originalValue: T[K];
      emptyValue?: T[K];
      message: string;
      error: boolean;
    };
  };
};

const mapPropsToState = <T extends Record<string, any>>(
  props: MappedProps<T>,
): StateType<T> => {
  return (Object.keys(props) as Array<keyof MappedProps<T>>).reduce(
    (current, key) => {
      return {
        ...current,
        fields: {
          ...current.fields,
          [key]: {
            message: "",
            value: getFieldValue(props[key].value),
            originalValue: getFieldValue(props[key].value),
            emptyValue: props[key].emptyValue,
            error: false,
          },
        },
      } as StateType<T>;
    },
    {
      unsavedChanges: new Set<string>([]),
      allValidated: false,
      fields: {},
    } as StateType<T>,
  );
};

export const mapObjectToProps = <P extends Record<string, any>>(object: P) => {
  return (Object.keys(object) as Array<keyof P>).reduce((acc, curr) => {
    acc[curr] = {
      value: object[curr],
    };
    return acc;
  }, {} as MappedProps<P>);
};

const useFormValidation = <P extends Record<string, any>>(
  _props: MappedProps<P>,
  options: Options = {},
): FormValidation<P> => {
  const props = useDeepCompareMemo(
    () => _props as Exclude<MappedProps<P>, void>,
    [_props],
  ) as MappedProps<P>;

  const reducer = (
    _state: StateType<P>,
    action: ReducerType<P>,
  ): StateType<P> => {
    switch (action.type) {
      case UPDATE_FIELD:
        return {
          ..._state,
          fields: {
            ..._state.fields,
            [action.payload.field]: {
              ..._state.fields[action.payload.field],
              ...action.payload.value,
            },
          },
        };
      case VALIDATE_FIELDS:
        return action.payload.reduce((current, key) => {
          const validationObj = props[key].validation ?? [];
          const validationResult = arrayValidateField(
            validationObj,
            _state.fields[key].value,
            getValues(_state, props, options),
          );
          if (
            validationResult.error === _state.fields[key].error &&
            validationResult.message === _state.fields[key].message
          )
            return current;
          return {
            ...current,
            fields: {
              ...current.fields,
              [key]: {
                ..._state.fields[key],
                error: validationResult.error,
                message: messageOrMessageFunctionOutput(
                  validationResult.message,
                  _state.fields[key].value,
                  getValues(_state, props, options),
                ),
              },
            },
          };
        }, _state);
      case RESET_FIELDS:
        action.payload.fields.forEach((key) => {
          _state.unsavedChanges.delete(key);
        });
        return {
          ..._state,
          fields: action.payload.fields.reduce(
            (acc, key) => ({
              ...acc,
              [key]: {
                ...acc[key],
                // eslint-disable-next-line no-nested-ternary
                value: action.payload.resetToEmptyValue
                  ? "emptyValue" in _state.fields[key]
                    ? _state.fields[key].emptyValue
                    : getEmptyValue(
                        _state.fields[key].value ??
                          _state.fields[key].originalValue,
                      )
                  : _state.fields[key].originalValue,
                error: false,
                message: "",
              },
            }),
            _state.fields,
          ),
        };
      case ADD_UNSAVEDCHANGES:
        return {
          ..._state,
          unsavedChanges: _state.unsavedChanges.add(action.payload),
        };
      case REMOVE_UNSAVEDCHANGES:
        _state.unsavedChanges.delete(action.payload);
        return { ..._state };
      case RESET_UNSAVEDCHANGES:
        return {
          ..._state,
          unsavedChanges: new Set([]),
        };
      case ALL_VALIDATED:
        return {
          ..._state,
          allValidated: true,
        };
      default:
        return _state;
    }
  };

  const [state, dispatch] = React.useReducer<
    React.Reducer<StateType<P>, ReducerType<P>>,
    MappedProps<P>
  >(reducer, props as MappedProps<P>, () => mapPropsToState(props));

  useDeepCompareEffect(() => {
    (Object.keys(props) as Array<keyof MappedProps<P>>).forEach((field) => {
      const nextValue = getFieldValue(props[field].value);
      if (!isEqual(nextValue, state.fields[field]?.originalValue)) {
        dispatch({
          type: UPDATE_FIELD,
          payload: {
            field,
            value: { value: nextValue, originalValue: nextValue },
          },
        });
      }
    });
  }, [props]);

  const saveChanges = (): void => {
    dispatch({ type: RESET_UNSAVEDCHANGES });
  };

  const setFieldErrors = (
    fields: Partial<Record<keyof P, boolean>> | ValidationProblemDetails,
  ): void => {
    if (isValidationProblemDetail(fields)) {
      Object.keys(fields.errors).forEach((field) => {
        if (state.fields[field]) {
          dispatch({
            type: UPDATE_FIELD,
            payload: {
              field,
              value: { error: true, message: fields.errors![field][0] },
            },
          });
        }
      });
    } else {
      Object.keys(fields).forEach((field) => {
        dispatch({
          type: UPDATE_FIELD,
          payload: { field, value: { error: fields[field] } },
        });
      });
    }
  };
  const values = React.useMemo(
    () => getValues(state, props, options),
    [options, props, state],
  );

  const validateFields = (fields?: Array<keyof P>): boolean => {
    dispatch({ type: ALL_VALIDATED });
    return Object.keys(state.fields || {}).reduce(
      (hasError: boolean, field: keyof P) => {
        if (!fields || fields.indexOf(field) !== -1) {
          const validationResult = validateField(
            props,
            field,
            state.fields[field].value,
            values,
          );
          dispatch({
            type: UPDATE_FIELD,
            payload: {
              field,
              value: {
                ...validationResult,
                message: messageOrMessageFunctionOutput(
                  validationResult.message,
                  state.fields[field].value,
                  values,
                ),
              },
            },
          });
          return hasError || validationResult.error;
        }

        dispatch({
          type: UPDATE_FIELD,
          payload: { field, value: { error: false } },
        });
        return hasError;
      },
      false,
    );
  };

  const resetFields = (
    fields?: Array<keyof P>,
    resetToEmptyValue = false,
  ): void => {
    const _fields = fields
      ? (fields as Array<string>).reduce<Array<keyof P>>((acc, field) => {
          if (field.indexOf("*") !== -1) {
            return [
              ...acc,
              ...Object.keys(state.fields || {}).filter((sf) =>
                sf.startsWith(field.replaceAll("*", "")),
              ),
            ];
          }
          return [...acc, field];
        }, [])
      : Object.keys(state.fields || {});
    dispatch({
      type: RESET_FIELDS,
      payload: { fields: _fields, resetToEmptyValue },
    });
  };

  const reset = (): void => {
    resetFields();
    saveChanges();
  };

  const wasFormValidOnMount = React.useMemo(() => {
    const initalState = mapPropsToState(props);
    return !Object.keys(state.fields || {}).some((field) => {
      return validateField(
        props,
        field,
        initalState.fields[field].value,
        values,
      ).error;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const valueHasChanged = (
    value: P[keyof P],
    originalValue: P[keyof P],
  ): boolean => {
    if (!originalValue) {
      if (
        !value ||
        (value as P[keyof P]) === "" ||
        (value as P[keyof P]) === "null" ||
        (value as P[keyof P]) === "undefined"
      )
        return false;
    }
    if (typeof value === "object" || typeof originalValue === "object") {
      return !deepEqual(value, originalValue);
    }
    return value !== originalValue;
  };

  const onBlur = (field: keyof P) => {
    return <T extends HTMLElement>(
      event: React.ChangeEvent<T> | P[keyof P] | undefined,
    ): void => {
      const value = getEventValue(event) as P[keyof P];
      const hasChanged = valueHasChanged(
        value,
        state.fields[field].originalValue,
      );

      dispatch({
        type: UPDATE_FIELD,
        payload: {
          field,
          value: {
            value,
          },
        },
      });

      const validateSelf =
        options.validateOnBlur !== state.allValidated &&
        (hasChanged || options.showErrorsOnNonDirtyFields);
      const fieldsToValidate =
        ((Object.keys(props) as Array<keyof MappedProps<P>>).filter(
          (k) =>
            (props[k].dependOn ?? []).filter((d) => d === field).length > 0,
        ) as Array<keyof P>) ?? [];
      dispatch({
        type: VALIDATE_FIELDS,
        payload: [validateSelf ? field : undefined, ...fieldsToValidate].filter(
          Boolean,
        ) as Array<keyof P>,
      });
      if (hasChanged) {
        dispatch({ type: ADD_UNSAVEDCHANGES, payload: field });
      } else {
        dispatch({ type: REMOVE_UNSAVEDCHANGES, payload: field });
      }
      if (options.onBlur) {
        options.onBlur();
      }
    };
  };

  const onChange = (field: keyof P) => {
    return <T extends HTMLElement>(
      event: React.ChangeEvent<T> | P[keyof P] | undefined,
    ): void => {
      const value = getEventValue(event) as P[keyof P];
      const hasChanged = valueHasChanged(
        value,
        state.fields[field].originalValue,
      );

      dispatch({
        type: UPDATE_FIELD,
        payload: {
          field,
          value: {
            value,
          },
        },
      });

      const validateSelf =
        options.validateOnChange !== state.allValidated &&
        (hasChanged || options.showErrorsOnNonDirtyFields);
      const fieldsToValidate =
        ((Object.keys(props) as Array<keyof MappedProps<P>>).filter(
          (k) =>
            (props[k].dependOn ?? []).filter((d) => d === field).length > 0,
        ) as Array<keyof P>) ?? [];
      dispatch({
        type: VALIDATE_FIELDS,
        payload: [validateSelf ? field : undefined, ...fieldsToValidate].filter(
          Boolean,
        ) as Array<keyof P>,
      });
      if (hasChanged) {
        dispatch({ type: ADD_UNSAVEDCHANGES, payload: field });
      } else {
        dispatch({ type: REMOVE_UNSAVEDCHANGES, payload: field });
      }
      if (options.onChange) {
        options.onChange();
      }
    };
  };

  const mapStateToProps = (): Fields<P> => {
    return (Object.keys(state.fields || {}) as Array<keyof Fields<P>>).reduce(
      (current, key) => {
        const stateItem = state.fields[key];
        return {
          ...current,
          [key]: {
            message: stateItem.message,
            value: stateItem.value,
            checked:
              (typeof stateItem.value === "boolean" &&
                stateItem.value === true) ||
              stateItem.value === "true",
            error: stateItem.error,
            onChange: onChange(key),
            onBlur: onBlur(key),
          } as Field<P[keyof Fields<P>]>,
        };
      },
      {} as Fields<P>,
    );
  };

  const isValid = React.useMemo(() => {
    return !Object.keys(state.fields || {}).some((field) => {
      return validateField(props, field, state.fields[field].value, values)
        .error;
    });
  }, [props, state.fields, values]);

  const fields = mapStateToProps();

  return {
    isValid,
    wasValidOnMount: wasFormValidOnMount,
    unsavedChanges: state.unsavedChanges && state.unsavedChanges.size > 0,
    fields,
    hasFields: Object.keys(fields).length > 0,
    validateFields,
    setFieldErrors,
    saveChanges,
    resetFields,
    reset,
    dispatch,
    values,
  };
};

export default useFormValidation;
